import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { ApiResponse } from './types';
import { baseQueryWithAuth } from './utils';
import type {
  WorkLogEntityType,
  WorkLogMetricsResponse,
  WorkLogResponse,
  WorkLogStatus,
  WorkLogSummaryResponse,
  WorkLogUpdateRequest,
  WorkLogBulkCreateRequest,
  FulcrumSyncStatus,
} from '@/generated/types/navigator/types.gen';
import { type WorklogFormProps } from '@/views/map/worklog/WorkLogAddOrEdit/hooks/useWorklogSubmit';
import { projectApi } from './projectSlice';

interface FetchWorklogs {
  entity_type: WorkLogEntityType;
  entity_id?: string;
  status?: string;
  date?: Date;
  worklogPage: number;
  last_updated?: Date;
  limit: number;
  plan_id?: string;
}

interface WorklogItem {
  level3: string;
  status: WorkLogStatus | null;
  id: string;
  entity_id?: string;
}

interface WorklogState {
  statusChanged: boolean;
  worklogItem: WorklogItem;
  worklogItems: WorkLogResponse[];
  worklogPage: number;
  worklogLayerData: string[];
}

const initialWorklogItemState: WorklogItem = {
  level3: '',
  status: null,
  id: '',
};

const initialState: WorklogState = {
  statusChanged: false,
  worklogItem: initialWorklogItemState,
  worklogPage: 1,
  worklogItems: [],
  worklogLayerData: [],
};

const sortItems = (items: WorkLogResponse[]) => {
  return items.sort((a, b) => {
    return (
      b.date.localeCompare(a.date) || b.updated_at.localeCompare(a.updated_at)
    );
  });
};

const worklogSlice = createSlice({
  name: 'worklog',
  initialState,
  reducers: {
    updateWorklogStatus: (state, action) => {
      state.statusChanged = action.payload;
    },
    setWorklogPage: (state, action) => {
      state.worklogPage = action.payload;
    },
    setWorklogItems: (state, action) => {
      state.worklogItems = action.payload;
    },
    addWorklogItems: (state, action: PayloadAction<WorkLogResponse[]>) => {
      const { entity_id, status, id } = action.payload[0];
      state.statusChanged = true;
      state.worklogItems = [...state.worklogItems, ...action.payload];
      state.worklogItems = sortItems(state.worklogItems);
      state.worklogItem = {
        level3: entity_id,
        status: status,
        id,
      };
    },
    updateWorklogItem: (state, action) => {
      const { id } = action.payload;
      state.statusChanged = true;
      state.worklogItems = state.worklogItems.map((s) => {
        if (s.id === id) {
          return action.payload;
        }
        return s;
      });
      state.worklogItems = sortItems(state.worklogItems);

      state.worklogItem = {
        level3: action.payload.entity_id,
        status: state.worklogItems.length ? state.worklogItems[0].status : null,
        id,
      };
    },
    deleteWorklogItem: (state, action) => {
      const { id, level3 } = action.payload;
      state.statusChanged = true;
      state.worklogItems = state.worklogItems.filter((item) => item.id !== id);
      const updatedStatus = state.worklogItems.length
        ? state.worklogItems[0].status
        : null;
      state.worklogItem = { level3, status: updatedStatus, id };
    },
  },
});

export const worklogApi = createApi({
  reducerPath: 'worklogApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['worklogData', 'fulcrumSync'],
  endpoints: (builder) => ({
    syncWorklogsWithFulcrum: builder.mutation<FulcrumSyncStatus, string>({
      query: (planId) => ({
        url: '/v1/fulcrum/sync',
        method: 'POST',
        params: { plan_id: planId },
      }),
      invalidatesTags: ['fulcrumSync'],
    }),
    fetchFulcrumSyncStatus: builder.query<FulcrumSyncStatus | null, string>({
      query: () => ({ url: '/v1/fulcrum/sync/status', cache: 'no-store' }),
      providesTags: ['fulcrumSync'],
    }),
    getWorkLogMetrics: builder.query<WorkLogMetricsResponse, string>({
      query: (planId) => ({
        url: '/v1/planner/work_log/metrics',
        params: { plan_id: planId },
      }),
      providesTags: [{ type: 'worklogData', id: 'LIST' }],
    }),
    fetchWorkLogs: builder.query<
      ApiResponse<WorkLogResponse, WorkLogSummaryResponse>,
      FetchWorklogs
    >({
      query: ({
        entity_type,
        worklogPage,
        limit,
        entity_id,
        plan_id,
      }: FetchWorklogs) => ({
        url: '/v1/planner/work_log/',
        params: { entity_type, entity_id, page: worklogPage, limit, plan_id },
      }),
      providesTags: (result) =>
        result && result.data
          ? [
              ...result.data.map((item) => ({
                type: 'worklogData' as const,
                id: item.id,
              })),
              { type: 'worklogData', id: 'LIST' },
            ]
          : [{ type: 'worklogData', id: 'LIST' }],
    }),
    addWorkLog: builder.mutation<WorkLogResponse, WorklogFormProps>({
      query: (body) => ({
        url: `/v1/planner/work_log/`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          ...body,
          date: body.date.toISOString(),
        },
      }),
      invalidatesTags: [{ type: 'worklogData', id: 'LIST' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(projectApi.util.invalidateTags(['project']));
      },
    }),

    updateWorkLog: builder.mutation<
      WorkLogUpdateRequest,
      { id: string; body: WorklogFormProps }
    >({
      query: ({ body, id }) => ({
        url: `/v1/planner/work_log/${id}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          ...body,
          date: body.date.toISOString(),
        },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'worklogData', id: 'LIST' },
        { type: 'worklogData', id },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(projectApi.util.invalidateTags(['project']));
      },
    }),
    deleteWorkLog: builder.mutation<string, string>({
      query: (id) => ({
        url: `/v1/planner/work_log/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        { type: 'worklogData', id: 'LIST' },
        { type: 'worklogData', id },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(projectApi.util.invalidateTags(['project']));
      },
    }),
    bulkAddWorkLog: builder.mutation<
      WorkLogResponse[],
      WorkLogBulkCreateRequest
    >({
      query: (body) => ({
        url: `/v1/planner/work_log/bulk`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      invalidatesTags: [{ type: 'worklogData', id: 'LIST' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(projectApi.util.invalidateTags(['project']));
      },
    }),
  }),
});

export const {
  useFetchWorkLogsQuery,
  useGetWorkLogMetricsQuery,
  useAddWorkLogMutation,
  useUpdateWorkLogMutation,
  useDeleteWorkLogMutation,
  useBulkAddWorkLogMutation,
  useSyncWorklogsWithFulcrumMutation,
  useFetchFulcrumSyncStatusQuery,
} = worklogApi;

export const resetWorklogApiState = worklogApi.util.resetApiState;

export const {
  updateWorklogStatus,
  setWorklogItems,
  addWorklogItems,
  updateWorklogItem,
  deleteWorklogItem,
  setWorklogPage,
} = worklogSlice.actions;

export default worklogSlice.reducer;
